/**
 * Reports store — Context + provider holding the tenant's saved reports.
 *
 * Mirrors `DashboardsProvider` (`lib/dashboard/store.tsx`) shape: in-memory
 * state, localStorage persistence (`divineiot:reports`), `version` counter
 * for memoised consumers, structural-damage guard via `isReportEditable()`.
 *
 * B2.0 ships save-only — no export pipeline yet. Mutations stay local;
 * Phase-1 swaps the storage layer for `/v1/twin/reports` CRUD without
 * changing the React API.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { GALLERY_REPORT_TEMPLATES } from "./fixtures";
import {
  isLiveApi,
  reports as reportsApi,
  toReportConfig,
  useEnabledPacksQuery,
} from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import {
  isReportEditable,
  type ReportConfig,
  type ReportFilters,
  type ReportNarrative,
  type ReportPage,
  type ReportSection,
} from "./types";

const STORAGE_KEY = "divineiot:reports";

export class ReportEditError extends Error {
  readonly code: "read_only" | "not_found";
  constructor(code: "read_only" | "not_found", message: string) {
    super(message);
    this.code = code;
    this.name = "ReportEditError";
  }
}

export { isReportEditable };

export interface ReportsState {
  reports: ReportConfig[];
  byId: (id: string) => ReportConfig | undefined;

  /** Create a new user-owned report. Returns the new id. */
  create: (
    seed: Omit<ReportConfig, "id" | "source" | "owner" | "createdAt" | "updatedAt"> & {
      id?: string;
      source?: "my" | "shared";
      owner?: string;
    },
  ) => string;

  /** Clone an existing report (pack or user). Always lands as user-owned. */
  clone: (sourceId: string, newName?: string) => string;

  /** Patch top-level report fields. Throws on pack-shipped. */
  update: (id: string, patch: Partial<ReportConfig>) => void;

  /** Remove a report (user-owned only). */
  remove: (id: string) => void;

  /** Set the narrative block. */
  setNarrative: (id: string, narrative: ReportNarrative) => void;

  /** Set the filter block. */
  setFilters: (id: string, filters: ReportFilters) => void;

  /** Add a page. */
  addPage: (id: string, page: ReportPage) => void;

  /** Remove a page. */
  removePage: (id: string, pageId: string) => void;

  /** Mutate one page (title, subtitle, sections). */
  updatePage: (id: string, pageId: string, patch: Partial<ReportPage>) => void;

  /** Add a section to a page. */
  addSection: (id: string, pageId: string, section: ReportSection) => void;

  /** Remove a section from a page. */
  removeSection: (id: string, pageId: string, sectionId: string) => void;

  /** Mutate one section. */
  updateSection: (
    id: string,
    pageId: string,
    sectionId: string,
    patch: Partial<ReportSection>,
  ) => void;

  /** Reset all user edits — restore the fixture seed. */
  reset: () => void;

  /** Mutation counter. */
  version: number;
}

const ReportsContext = createContext<ReportsState | null>(null);

export function useReports(): ReportsState {
  const ctx = useContext(ReportsContext);
  if (!ctx) {
    throw new Error("useReports must be used inside <ReportsProvider>");
  }
  return ctx;
}

export function useOptionalReports(): ReportsState | null {
  return useContext(ReportsContext);
}

function readPersisted(): ReportConfig[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed as ReportConfig[];
  } catch {
    return null;
  }
}

function writePersisted(reports: ReportConfig[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(reports));
  } catch {
    /* swallow */
  }
}

/**
 * Mint a new report id. In live mode the id is the durable backend's PK
 * (uuid column), so use a real UUID — the SPA owns it (create/clone are
 * synchronous) and the server upserts on it. Stub mode keeps the legacy
 * `rep-…` ids (localStorage only).
 */
function newReportId(): string {
  if (isLiveApi && typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `rep-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

/**
 * Seed the provider with pack-shipped templates merged with any persisted
 * user-owned reports. Pack templates always survive across reloads — they
 * come from `GALLERY_REPORT_TEMPLATES`, not localStorage.
 */
function seedReports(): ReportConfig[] {
  // Live (multi-tenant) mode: no client-side pack templates or global
  // localStorage — both leaked the foundry demo reports into every org.
  // Reports aren't org-scoped server-side yet, so start empty.
  if (isLiveApi) return [];
  const persisted = readPersisted() ?? [];
  // User-owned + shared reports from localStorage; pack templates come fresh
  // from fixtures so we never persist a stale copy.
  const userOwned = persisted.filter((r) => r.source !== "pack");
  return [...GALLERY_REPORT_TEMPLATES, ...userOwned];
}

export function ReportsProvider({ children }: { children: ReactNode }) {
  const [reports, setReports] = useState<ReportConfig[]>(() => seedReports());
  const [version, setVersion] = useState(0);

  // Live mode: which pack templates to surface as read-only starters. The
  // foundry pack ships 3 report templates; show them only to orgs that have
  // the foundry pack enabled (server truth) — they were hidden in live mode
  // precisely because showing them to *every* org leaked foundry content.
  // Stub mode seeds templates via seedReports() above, so this gate is
  // live-only.
  const enabledPacks = useEnabledPacksQuery();
  const foundryEnabled =
    isLiveApi && (enabledPacks.data?.packs ?? []).some((p) => p.pack === "foundry");
  const { activeOrgId } = useOrg();

  // Live mode: hydrate user-owned reports from the durable backend
  // (/v1/twin/reports) for the ACTIVE org. Keyed on activeOrgId so it re-runs on
  // an org switch: drop the previous org's user-owned reports FIRST (keep pack-
  // template starters), then load this org's. Without the reset, org A's reports
  // lingered under org B and a debounced PATCH could persist them under org B's
  // X-Org-Id (#92). On failure we keep the pack templates rather than wiping.
  useEffect(() => {
    if (!isLiveApi) return;
    setReports((prev) => prev.filter((r) => r.source === "pack"));
    if (!activeOrgId) return;
    let cancelled = false;
    reportsApi
      .list()
      .then((res) => {
        if (cancelled) return;
        const own = (res.items ?? []).map(toReportConfig);
        setReports((prev) => [...prev.filter((r) => r.source === "pack"), ...own]);
      })
      .catch(() => {
        /* keep pack templates */
      });
    return () => {
      cancelled = true;
    };
  }, [activeOrgId]);

  // Live mode: keep the foundry pack-template starters in sync with the org's
  // entitlement. Idempotent — strip any existing pack rows, then re-add the
  // fixtures iff foundry is on. Runs on mount + whenever entitlement resolves
  // (the packs query is async), so it's robust to load ordering.
  useEffect(() => {
    if (!isLiveApi) return;
    setReports((prev) => {
      const own = prev.filter((r) => r.source !== "pack");
      return foundryEnabled ? [...GALLERY_REPORT_TEMPLATES, ...own] : own;
    });
  }, [foundryEnabled]);

  useEffect(() => {
    // Persist only user-owned reports; pack templates are seeded from code.
    // Skip entirely in live mode (server-backed via /v1/twin/reports).
    if (!isLiveApi) writePersisted(reports.filter((r) => r.source !== "pack"));
  }, [reports]);

  const bumpVersion = useCallback(() => setVersion((v) => v + 1), []);

  // Debounced server persistence (live mode only). Narrative edits fire
  // per-keystroke, so we coalesce to one PATCH per report ~700ms after the
  // last change. The whole config blob is sent each time (the server upserts
  // it), so collapsing to the latest snapshot is correct. create/clone POST
  // immediately (one-shot); remove cancels any pending save then DELETEs (so
  // an in-flight upsert can't resurrect a just-deleted report).
  const persistTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const schedulePersist = useCallback((rep: ReportConfig) => {
    if (!isLiveApi) return;
    const timers = persistTimers.current;
    if (timers[rep.id]) clearTimeout(timers[rep.id]);
    timers[rep.id] = setTimeout(() => {
      delete timers[rep.id];
      void reportsApi.update(rep.id, { name: rep.name, config: rep }).catch(() => {});
    }, 700);
  }, []);
  const cancelPersist = useCallback((id: string) => {
    const timers = persistTimers.current;
    if (timers[id]) {
      clearTimeout(timers[id]);
      delete timers[id];
    }
  }, []);

  const byId = useCallback(
    (id: string) => reports.find((r) => r.id === id),
    [reports],
  );

  const create = useCallback<ReportsState["create"]>(
    (seed) => {
      const id = seed.id ?? newReportId();
      const now = new Date().toISOString();
      const next: ReportConfig = {
        id,
        name: seed.name,
        description: seed.description,
        pack: seed.pack,
        templateId: seed.templateId,
        source: seed.source ?? "my",
        owner: seed.owner ?? "you",
        createdAt: now,
        updatedAt: now,
        pages: seed.pages,
        narrative: seed.narrative,
        filters: seed.filters,
      };
      setReports((prev) => [next, ...prev]);
      bumpVersion();
      // Live mode: persist the whole config blob (the mapper hydrates it back
      // on load; the envelope id/name/updated_at win). Fire-and-forget — a
      // failed write degrades to local-only, it doesn't block authoring.
      if (isLiveApi) {
        void reportsApi.create({ id, name: next.name, config: next }).catch(() => {});
      }
      return id;
    },
    [bumpVersion],
  );

  const clone = useCallback<ReportsState["clone"]>(
    (sourceId, newName) => {
      const src = reports.find((r) => r.id === sourceId);
      if (!src) {
        throw new ReportEditError("not_found", `Report "${sourceId}" not found`);
      }
      const id = newReportId();
      const now = new Date().toISOString();
      // Deep clone via JSON to avoid shared widget-instance refs.
      const cloned: ReportConfig = JSON.parse(JSON.stringify(src));
      cloned.id = id;
      cloned.name = newName ?? `${src.name} (copy)`;
      cloned.source = "my";
      cloned.owner = "you";
      cloned.createdAt = now;
      cloned.updatedAt = now;
      cloned.templateId = src.id;
      // Re-id pages + sections so future edits target the new copy.
      cloned.pages = cloned.pages.map((p) => ({
        ...p,
        id: `pg-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        sections: p.sections.map((s) => ({
          ...s,
          id: `sec-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        })),
      }));
      setReports((prev) => [cloned, ...prev]);
      bumpVersion();
      if (isLiveApi) {
        void reportsApi.create({ id, name: cloned.name, config: cloned }).catch(() => {});
      }
      return id;
    },
    [reports, bumpVersion],
  );

  const update = useCallback<ReportsState["update"]>(
    (id, patch) => {
      const target = reports.find((r) => r.id === id);
      if (target && !isReportEditable(target)) {
        throw new ReportEditError(
          "read_only",
          `Report "${target.name}" is pack-shipped — clone it first to edit.`,
        );
      }
      const now = new Date().toISOString();
      setReports((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ...patch, updatedAt: now } : r)),
      );
      bumpVersion();
      if (isLiveApi && target) {
        schedulePersist({ ...target, ...patch, updatedAt: now });
      }
    },
    [reports, bumpVersion, schedulePersist],
  );

  const remove = useCallback<ReportsState["remove"]>(
    (id) => {
      const target = reports.find((r) => r.id === id);
      if (target && !isReportEditable(target)) {
        throw new ReportEditError(
          "read_only",
          `Report "${target.name}" is pack-shipped and cannot be deleted.`,
        );
      }
      setReports((prev) => prev.filter((r) => r.id !== id));
      bumpVersion();
      if (isLiveApi && target) {
        cancelPersist(id);
        void reportsApi.remove(id).catch(() => {});
      }
    },
    [reports, bumpVersion, cancelPersist],
  );

  /** Mutate one report's pages list, with read-only + not-found guards. */
  const mutateReport = useCallback(
    (id: string, fn: (r: ReportConfig) => ReportConfig) => {
      const target = reports.find((r) => r.id === id);
      if (target && !isReportEditable(target)) {
        throw new ReportEditError(
          "read_only",
          `Report "${target.name}" is pack-shipped — clone it first to edit.`,
        );
      }
      const now = new Date().toISOString();
      setReports((prev) =>
        prev.map((r) => (r.id === id ? { ...fn(r), updatedAt: now } : r)),
      );
      bumpVersion();
      if (isLiveApi && target) {
        schedulePersist({ ...fn(target), updatedAt: now });
      }
    },
    [reports, bumpVersion, schedulePersist],
  );

  const setNarrative = useCallback<ReportsState["setNarrative"]>(
    (id, narrative) => mutateReport(id, (r) => ({ ...r, narrative })),
    [mutateReport],
  );

  const setFilters = useCallback<ReportsState["setFilters"]>(
    (id, filters) => mutateReport(id, (r) => ({ ...r, filters })),
    [mutateReport],
  );

  const addPage = useCallback<ReportsState["addPage"]>(
    (id, page) => mutateReport(id, (r) => ({ ...r, pages: [...r.pages, page] })),
    [mutateReport],
  );

  const removePage = useCallback<ReportsState["removePage"]>(
    (id, pageId) =>
      mutateReport(id, (r) => ({
        ...r,
        pages: r.pages.filter((p) => p.id !== pageId),
      })),
    [mutateReport],
  );

  const updatePage = useCallback<ReportsState["updatePage"]>(
    (id, pageId, patch) =>
      mutateReport(id, (r) => ({
        ...r,
        pages: r.pages.map((p) => (p.id === pageId ? { ...p, ...patch } : p)),
      })),
    [mutateReport],
  );

  const addSection = useCallback<ReportsState["addSection"]>(
    (id, pageId, section) =>
      mutateReport(id, (r) => ({
        ...r,
        pages: r.pages.map((p) =>
          p.id === pageId ? { ...p, sections: [...p.sections, section] } : p,
        ),
      })),
    [mutateReport],
  );

  const removeSection = useCallback<ReportsState["removeSection"]>(
    (id, pageId, sectionId) =>
      mutateReport(id, (r) => ({
        ...r,
        pages: r.pages.map((p) =>
          p.id === pageId
            ? { ...p, sections: p.sections.filter((s) => s.id !== sectionId) }
            : p,
        ),
      })),
    [mutateReport],
  );

  const updateSection = useCallback<ReportsState["updateSection"]>(
    (id, pageId, sectionId, patch) =>
      mutateReport(id, (r) => ({
        ...r,
        pages: r.pages.map((p) =>
          p.id === pageId
            ? {
                ...p,
                sections: p.sections.map((s) =>
                  s.id === sectionId
                    ? ({ ...s, ...patch } as ReportSection)
                    : s,
                ),
              }
            : p,
        ),
      })),
    [mutateReport],
  );

  const reset = useCallback(() => {
    setReports(isLiveApi ? [] : GALLERY_REPORT_TEMPLATES);
    bumpVersion();
  }, [bumpVersion]);

  const value = useMemo<ReportsState>(
    () => ({
      reports,
      byId,
      create,
      clone,
      update,
      remove,
      setNarrative,
      setFilters,
      addPage,
      removePage,
      updatePage,
      addSection,
      removeSection,
      updateSection,
      reset,
      version,
    }),
    [
      reports,
      byId,
      create,
      clone,
      update,
      remove,
      setNarrative,
      setFilters,
      addPage,
      removePage,
      updatePage,
      addSection,
      removeSection,
      updateSection,
      reset,
      version,
    ],
  );

  return (
    <ReportsContext.Provider value={value}>{children}</ReportsContext.Provider>
  );
}
